import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Usuario, LoginCredentials, AuthResponse } from '../../domain/usuario.domain';

export interface DecodedToken {
  exp: number;
  iat: number;
  roles: string[];
  username: string;
  userId: number;
  [key: string]: any;
}

/**
 * Interface para login (compatible con backend)
 */
export interface UserLogin {
  email?: string;
  password?: string;
  remember?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class SecurityService {
  private router = inject(Router);
  private http = inject(HttpClient);

  private readonly TOKEN_KEY = 'token';
  private readonly USER_KEY = 'usuario';
  private readonly ID_KEY = 'id';

  private usuarioSubject!: BehaviorSubject<Usuario>;
  readonly usuario$!: Observable<Usuario>;
  public usuarioLogged: Usuario = {};
  private readonly staticBaseUrl: string;

  private get serviceBaseURL(): string {
    return environment.URL_API;
  }

  /**
   * Obtiene la URL base para recursos estáticos (uploads, imágenes, etc.)
   * Elimina /api y /index.php de la URL_API para obtener la base correcta
   * Ejemplos:
   * - https://alttab.ar/publicidad/lt10/lt10-ms/public/index.php/api -> https://alttab.ar/publicidad/lt10/lt10-ms/public
   * - http://localhost:250/api -> http://localhost:250
   */
  getStaticBaseUrl(): string {
    return this.staticBaseUrl;
  }

  constructor() {
    this.staticBaseUrl = this.computeStaticBaseUrl(environment.URL_API);
    if (environment.servicedebug) {
      console.log('🔧 getStaticBaseUrl - URL_API original:', environment.URL_API);
      console.log('🔧 getStaticBaseUrl - URL base resultante:', this.staticBaseUrl);
    }

    let usuario: any = localStorage.getItem(this.USER_KEY);
    usuario = usuario ? JSON.parse(usuario) : {};
    
    this.usuarioSubject = new BehaviorSubject<Usuario>(usuario);
    this.usuario$ = this.usuarioSubject.asObservable();
    
    this.usuario$.subscribe((user: Usuario) => {
      this.usuarioLogged = user;
    });
  }

  private computeStaticBaseUrl(urlApi: string): string {
    let baseUrl = urlApi;
    baseUrl = baseUrl.replace(/\/index\.php\/api\/?$/, '');
    baseUrl = baseUrl.replace(/\/api\/?$/, '');
    baseUrl = baseUrl.replace(/\/index\.php\/?$/, '');
    baseUrl = baseUrl.replace(/\/$/, '');
    return baseUrl;
  }
  
  public get usuarioValue(): Usuario {
    return this.usuarioSubject.getValue() ? this.usuarioSubject.getValue() : this.usuarioLogged;
  }

  /**
   * Guarda el token en localStorage
   */
  setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  /**
   * Obtiene el token de localStorage
   */
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Elimina el token de localStorage
   */
  removeToken(): void {
    localStorage.removeItem(this.TOKEN_KEY);
  }

  /**
   * Guarda la información del usuario en localStorage
   */
  setUser(user: any): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  /**
   * Obtiene la información del usuario de localStorage
   */
  getUser(): any {
    const user = localStorage.getItem(this.USER_KEY);
    return user ? JSON.parse(user) : null;
  }

  /**
   * Elimina la información del usuario de localStorage
   */
  removeUser(): void {
    localStorage.removeItem(this.USER_KEY);
  }

  /**
   * Decodifica un JWT token
   */
  decodeToken(token: string): DecodedToken | null {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  /**
   * Verifica si el token ha expirado
   */
  isTokenExpired(): boolean {
    const token = this.getToken();
    if (!token) {
      return true;
    }

    const decoded = this.decodeToken(token);
    if (!decoded || !decoded.exp) {
      return true;
    }

    const expirationDate = new Date(decoded.exp * 1000);
    return expirationDate < new Date();
  }

  /**
   * Obtiene los roles del usuario actual
   */
  getUserRoles(): string[] {
    const token = this.getToken();
    if (!token) {
      return [];
    }

    const decoded = this.decodeToken(token);
    return decoded?.roles || [];
  }

  /**
   * Verifica si el usuario tiene un rol específico
   */
  hasRole(role: string): boolean {
    const roles = this.getUserRoles();
    return roles.includes(role);
  }

  /**
   * Verifica si el usuario tiene alguno de los roles especificados
   */
  hasAnyRole(roles: string[]): boolean {
    const userRoles = this.getUserRoles();
    return roles.some(role => userRoles.includes(role));
  }

  /**
   * Verifica si el usuario tiene todos los roles especificados
   */
  hasAllRoles(roles: string[]): boolean {
    const userRoles = this.getUserRoles();
    return roles.every(role => userRoles.includes(role));
  }

  /**
   * Verifica si el usuario está autenticado
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    return !!token && !this.isTokenExpired();
  }

  /**
   * Verifica si el usuario es administrador
   */
  isAdmin(): boolean {
    return this.hasAnyRole([environment.ROLE_ADMIN, environment.ROLE_SUPER_ADMIN]);
  }

  /**
   * Verifica si el usuario es super administrador
   */
  isSuperAdmin(): boolean {
    return this.hasRole(environment.ROLE_SUPER_ADMIN);
  }

  /**
   * Obtiene el ID del usuario actual
   */
  getUserId(): number | null {
    const token = this.getToken();
    if (!token) {
      return null;
    }

    const decoded = this.decodeToken(token);
    return decoded?.userId || null;
  }

  /**
   * Obtiene el username del usuario actual
   */
  getUsername(): string | null {
    const token = this.getToken();
    if (!token) {
      return null;
    }

    const decoded = this.decodeToken(token);
    return decoded?.username || null;
  }

  /**
   * Inicia sesión del usuario
   */
  login(usuario: UserLogin, recordar: boolean = false): Observable<Usuario> {
    if (recordar) {
      localStorage.setItem('email', usuario.email ?? '');
    } else {
      localStorage.removeItem('email');
    }

    const url = `${this.serviceBaseURL}/login_check`;

    return this.http.post(url, usuario).pipe(
      map((data: any) => {
        const user: Usuario = data.user as Usuario;
        this.saveLocalStorage(data.token, user);
        this.usuarioSubject.next(user);
        return user;
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Verifica si el usuario está logueado
   */
  get isLoggedIn(): boolean {
    let usuario: any = localStorage.getItem(this.USER_KEY);

    if (usuario !== null && usuario !== undefined) {
      usuario = JSON.parse(usuario) as Usuario;
    }

    const tokenExpired = this.tokenExpired(this.getToken() ?? '');
    return usuario !== null && !tokenExpired;
  }

  /**
   * Verifica si el token ha expirado (método interno)
   */
  private tokenExpired(token: string): boolean {
    if (!token) {
      return true;
    }
    try {
      const expiry = JSON.parse(atob(token.split('.')[1])).exp;
      return Math.floor(new Date().getTime() / 1000) >= expiry;
    } catch (e) {
      return true;
    }
  }

  /**
   * Guarda el token y usuario en localStorage
   */
  private saveLocalStorage(token: string, usuario: Usuario): void {
    localStorage.setItem(this.ID_KEY, usuario.id ?? 'null');
    localStorage.setItem(this.TOKEN_KEY, token);
    localStorage.setItem(this.USER_KEY, JSON.stringify(usuario));
  }

  /**
   * Obtiene los datos del usuario logueado desde el backend
   */
  getUserMe(): Observable<Usuario> {
    const url = `${this.serviceBaseURL}/user/me`;

    return this.http.get(url).pipe(
      map((resp: any) => {
        this.usuarioSubject.next(resp as Usuario);
        return resp;
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Actualiza el usuario en el BehaviorSubject y localStorage
   */
  private updateUser(user: Usuario): void {
    console.log('🔄 Actualizando usuario en SecurityService:', user);
    console.log('🖼️ Media del usuario:', user.media);
    this.usuarioSubject.next(user);
    this.setUser(user);
    console.log('💾 Usuario guardado en localStorage:', JSON.parse(localStorage.getItem(this.USER_KEY) || '{}'));
  }

  /**
   * Sube un avatar para el usuario
   */
  subirAvatar(userId: string, formData: FormData): Observable<any> {
    const url = `${this.serviceBaseURL}/users/${userId}/upload-avatar`;
    console.log('📤 Subiendo avatar para usuario ID:', userId);
    return this.http.post(url, formData).pipe(
      map((resp: any) => {
        console.log('📥 Respuesta del servidor después de subir avatar:', resp);
        console.log('🆔 Usuario ID que subió:', userId);
        console.log('🆔 Usuario logueado ID:', this.usuarioLogged.id);
        console.log('🔍 ¿Son iguales?:', userId === this.usuarioLogged.id);
        
        // Si el usuario es el mismo logueado actualizo las variables de storage
        if (userId === this.usuarioLogged.id || userId === String(this.usuarioLogged.id)) {
          const usuarioDB: Usuario = resp;
          console.log('✅ Actualizando usuario logueado con nueva data');
          this.updateUser(usuarioDB);
          const token = this.getToken();
          if (token) {
            this.saveLocalStorage(token, usuarioDB);
          }
        } else {
          console.warn('⚠️ Los IDs no coinciden, no se actualizó el usuario logueado');
        }
        return resp;
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Actualiza la contraseña de un usuario (cambio por el propio usuario)
   */
  actualizarPasswordUsuario(usuarioParam: Usuario): Observable<any> {
    const url = `${this.serviceBaseURL}/users/${usuarioParam.id}/reset-password`;

    const passwordData = {
      oldPassword: usuarioParam.oldPassword,
      newPassword: usuarioParam.newPassword,
      newRetypedPassword: usuarioParam.newRetypedPassword
    };

    // API Platform 4 requiere application/merge-patch+json para PATCH
    const headers = new HttpHeaders({
      'Content-Type': 'application/merge-patch+json'
    });

    return this.http.patch(url, passwordData, { headers }).pipe(
      map((resp: any) => {
        return resp;
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Fuerza el cambio de contraseña de un usuario (por un administrador)
   */
  forzarCambioPassword(usuarioId: string, passwordData: any): Observable<any> {
    // Usar el mismo endpoint reset-password pero sin oldPassword
    const url = `${this.serviceBaseURL}/users/${usuarioId}/reset-password`;

    // Mapear los campos de "force" a los que espera el backend
    const payload = {
      newPassword: passwordData.forceNewPassword,
      newRetypedPassword: passwordData.forceRetypedPassword
      // NO se envía oldPassword - esto permite forzar el cambio
    };

    // API Platform 4 requiere application/merge-patch+json para PATCH
    const headers = new HttpHeaders({
      'Content-Type': 'application/merge-patch+json'
    });

    return this.http.patch(url, payload, { headers }).pipe(
      map((resp: any) => {
        return resp;
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Cambia la contraseña del usuario con contraseña por defecto
   * NO requiere oldPassword
   */
  cambiarPasswordObligatorio(newPassword: string, newRetypedPassword: string): Observable<any> {
    const userId = this.usuarioLogged.id;
    const url = `${this.serviceBaseURL}/users/${userId}/reset-password`;

    const payload = {
      newPassword,
      newRetypedPassword
      // NO enviar oldPassword
    };

    // API Platform 4 requiere application/merge-patch+json para PATCH
    const headers = new HttpHeaders({
      'Content-Type': 'application/merge-patch+json'
    });

    return this.http.patch(url, payload, { headers }).pipe(
      map((resp: any) => {
        // Backend actualiza is_default_password=0 automáticamente
        // Refrescar usuario en localStorage con datos actualizados del backend
        if (resp && resp.user) {
          const usuarioActualizado: Usuario = resp.user;
          this.updateUser(usuarioActualizado);
          const token = this.getToken();
          if (token) {
            this.saveLocalStorage(token, usuarioActualizado);
          }
        } else {
          // Si el backend no devuelve el usuario actualizado,
          // actualizar manualmente is_default_password
          const usuarioActualizado = {
            ...this.usuarioLogged,
            is_default_password: 0
          };
          this.updateUser(usuarioActualizado);
          const token = this.getToken();
          if (token) {
            this.saveLocalStorage(token, usuarioActualizado);
          }
        }
        return resp;
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Verifica si el usuario actual tiene contraseña por defecto
   */
  hasDefaultPassword(): boolean {
    const user = this.usuarioLogged;
    return user?.is_default_password === 1 || user?.is_default_password === true;
  }

  /**
   * Cierra sesión del usuario
   */
  logout(): void {
    localStorage.removeItem(this.ID_KEY);
    this.removeToken();
    this.removeUser();
    this.usuarioSubject.next({});
    this.router.navigate(['/auth/login']);
  }

  /**
   * Solicita el restablecimiento de contraseña
   * @param email Email del usuario
   */
  forgotPassword(email: string): Observable<any> {
    const url = `${this.serviceBaseURL}/user/forgotpassword`;
    return this.http.post(url, { email }).pipe(
      map((resp: any) => {
        return resp;
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Restablece la contraseña con el token recibido por email
   * @param token Token de recuperación
   * @param newPassword Nueva contraseña
   */
  resetPassword(token: string, newPassword: string): Observable<any> {
    const url = `${this.serviceBaseURL}/reset-password`;
    return this.http.post(url, { token, password: newPassword }).pipe(
      map((resp: any) => {
        return resp;
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Maneja errores de las peticiones HTTP
   */
  private handleError(err: HttpErrorResponse): Observable<never> {
    let errorMessage = '';
    if (err.error instanceof ErrorEvent) {
      errorMessage = `An error occurred: ${err.error.message}`;
    } else {
      errorMessage = `Server returned code: ${err.status}, error message is: ${err.message}`;
    }
    console.error(errorMessage);
    return throwError(() => err);
  }

  /**
   * Verifica si el usuario tiene permisos jerárquicos
   */
  hasHierarchicalRole(role: string): boolean {
    const userRoles = this.getUserRoles();
    const hierarchy = environment.ROLES_JERARQUIA as { [key: string]: string[] };

    for (const userRole of userRoles) {
      if (hierarchy[userRole]?.includes(role)) {
        return true;
      }
    }

    return false;
  }
}
